<template>
	<div>
		<div class="d-flex justify-content-between ts-mb-3">
			<div>
				<h1 class="page-header ts-mb-0">
					{{ $t('currency.pageTitle') }}
				</h1>
			</div>
			<ts-breadcrumb :items="[
				{ text: $t('toHomePage'), href: '/' },
				{
					text: $t('currency.pageTitle'),
					active: true
				}
			]" />
		</div>
		<ts-card>
			<ts-card-wrapper>
				<div class="ts-flex ts-justify-between ts-items-center">
					<div>
						<ts-button color="info" outline @click="addNew">
							{{ $t('add_new') }}
						</ts-button>
					</div>
					<div>
						<a-input-search v-model="search" @search="fetchData()"
							:placeholder="$t('currency.search')"
						></a-input-search>
					</div>
				</div>
			</ts-card-wrapper>
			<div class="overflow-auto ts-mt-4">
				<Table :columns="columns" :data="resources" :loading="loading" max-height="700" stripe highlight-row
					size="small">
					<template slot-scope="{ row }" slot="is_default">
						<div v-if="row.is_default" class="ts-w-12 ts-px-3 ts-py-1 ts-rounded-md ts-bg-green-200 ts-text-green-500">
							Yes</div>
						<div v-else class="ts-w-12 ts-px-3 ts-py-1 ts-rounded-md ts-bg-red-200 ts-text-red-500">No</div>
					</template>
					<template slot-scope="{ row }" slot="action">
						<div class="ts-flex ts-space-x-2 ts-items-center">
							<a href="#" v-tooltip="$t('edit')"
								class="text-primary ts-h-6 ts-w-6 ts-flex ts-items-center ts-justify-center ts-bg-blue-100 ts-rounded-full hover:ts-no-underline"
								@click.prevent="edit(row)">
								<i class="fas fa-pen"></i>
							</a>
							<a-popconfirm placement="topLeft" :ok-text="$t('yes')" :cancel-text="$t('no')"
								@confirm="clickDeleteHandler(row)">
								<template slot="title">
									<p>
										{{ $t('are_you_sure_to_delete') }}
									</p>
								</template>
								<a href="#" v-tooltip="$t('delete')"
									class="ts-text-red-500 ts-h-6 ts-w-6 ts-flex ts-items-center ts-justify-center ts-bg-red-100 ts-rounded-full hover:ts-no-underline hover:ts-text-red-500"
									:disabled="row._deleting">
									<i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
									<i class="fas fa-trash" v-else></i>
								</a>
							</a-popconfirm>
						</div>
					</template>
				</Table>
			</div>
			<div class="d-flex justify-content-between align-items-center p-15">
				<ts-total-list v-model="pagination" />
				<ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
			</div>
		</ts-card>
		<a-modal :visible="showForm" :footer="null" centered :title="$t('currency.pageTitle')" @cancel="onCancel">
			<ts-loading-banner :loading="saving">
				<div class="ts-space-y-2">
					<div class="row">
						<div class="col-md-12">
							<label class="required">{{ $t('currency.code') }}</label>
							<a-input v-model="model.currency_code" placeholder="USD|KHR|THB"></a-input>
							<div v-if="errors.has('currency_code')" class="ts-text-red-500 ts-text-xs">
								{{ errors.first('currency_code') }}
							</div>
						</div>
					</div>
					<div class="row">
						<div class="col-md-12">
							<label class="required">{{ $t('currency.symbol') }}</label>
							<a-input v-model="model.currency_symbol" placeholder="$|៛|฿"></a-input>
							<div v-if="errors.has('currency_symbol')" class="ts-text-red-500 ts-text-xs">
								{{ errors.first('currency_symbol') }}
							</div>
						</div>
					</div>
					<div class="row">
						<div class="col-md-12">
							<ts-checkbox v-model="model.is_default">{{ $t('currency.default') }}</ts-checkbox>
						</div>
					</div>

					<div class="row">
						<div class="col-md-12 ts-flex ts-justify-end ts-space-x-2">
							<ts-button @click="onCancel">{{ $t('cancel') }}</ts-button>
							<ts-button v-if="!isEdit" outline color="primary" @click="clickSaveAddNewHandler">{{ $t('save_add_new') }}</ts-button>
							<ts-button v-if="!isEdit" color="primary" @click="clickSaveHandler">{{ $t('save') }}</ts-button>
							<ts-button color="primary" v-if="isEdit" @click="clickUpdateHandler">{{ $t('update') }}</ts-button>
						</div>
					</div>
				</div>
			</ts-loading-banner>
		</a-modal>

	</div>
</template>

<script>
import notificationAlert from '@/utils/NotificationAlert';
import { Errors } from 'form-backend-validation';
import { mapActions, mapState } from 'vuex';

export default {
	name: "currency",
	data() {
		return {
			loading: false,
			showForm: false,
			saving: false,
			errors: new Errors(),
			isEdit: false,
			model: {
				id: undefined,
				currency_code: "",
				currency_symbol: "",
				is_default: false
			}
		}
	},
	computed: {
		...mapState('currency', [
			'resources',
			'pagination',
		]),
		search: {
			get() {
				return this.$store.state.currency.search
			},
			set(newValue) {
				this.$store.commit('currency/SET_SEARCH', newValue)
				this.$store.commit('currency/RESET_CURRENT_PAGE')
			}
		},
		columns() {
			return [
				{
					title: this.$t('currency.code'),
					key: 'currency_code'
				},
				{
					title: this.$t('currency.symbol'),
					key: 'currency_symbol'
				},
				{
					title: this.$t('currency.default'),
					slot: 'is_default'
				},
				{
					title: this.$t('currency.createdAt'),
					key: 'created_date'
				},
				{
					title: this.$t('currency.modifiedAt'),
					key: 'modified_date'
				},
				{
					title: this.$t('actions'),
					slot: 'action',
					fixed: 'right',
					align: 'center',
					width: 100
				}
			]
		}
	},
	mounted() {
		this.fetchData()
	},
	methods: {
		...mapActions('currency', ['fetch', 'onDelete', 'onSave', 'onUpdate']),
		addNew() {
			this.clearInput();
			this.showForm = true
		},
		async fetchData(attributes = {}) {
			this.loading = true
			return await this.fetch(attributes)
				.catch(error => {
					notificationAlert(error)
				})
				.finally(() => {
					this.loading = false
				})
		},
		clickSaveAddNewHandler() {
			this.saving = true
			this.onSave(this.model)
				.then((response) => {
					notificationAlert(response, 'success')
					this.clearInput()
					this.fetchData()
				})
				.catch((errror) => {
					notificationAlert(errror)
					this.errors = new Errors(errror.response.data.errors)
				})
				.finally(() => this.saving = false)
		},		
		clickSaveHandler() {
			this.saving = true
			this.onSave(this.model)
				.then((response) => {
					notificationAlert(response, 'success')
					this.fetchData()
					this.clearInput()
					this.showForm = false
				})
				.catch((errror) => {
					notificationAlert(errror)
					this.errors = new Errors(errror.response.data.errors)
				})
				.finally(() => this.saving = false)
		},
		clickUpdateHandler() {
			this.saving = true
			this.onUpdate(this.model)
				.then((response) => {
					notificationAlert(response, 'success')
					this.fetchData()
					this.clearInput()
					this.showForm = false
				})
				.catch((errror) => {
					notificationAlert(errror)
					this.errors = new Errors(errror.response.data.errors)
				})
				.finally(() => this.saving = false)
		},
		clickDeleteHandler(record) {
			this.saving = true
			this.onDelete(record.currency_id)
				.then((response) => {
					notificationAlert(response, 'success')
					this.fetchData()
				})
				.catch((errror) => {
					notificationAlert(errror)
					this.errors = new Errors(errror.response.data.errors)
				})
				.finally(() => this.saving = false)
		},
		edit(record) {
			const { currency_id, currency_code, currency_symbol, is_default } = record
			this.model.id = currency_id
			this.model.currency_symbol = currency_symbol
			this.model.currency_code = currency_code
			this.model.is_default = is_default
			this.showForm = true
			this.isEdit = true
		},
		clearInput() {
			this.model.id = undefined
			this.model.currency_code = ""
			this.model.currency_symbol = ""
			this.model.is_default = false
		},
		onCancel() {
			this.showForm = false
			this.clearInput()
			this.fetchData()
		}
	}
}
</script>